import type {} from "./types.d.ts";
import { createInstance, type i18n } from "i18next";
import { initReactI18next } from "react-i18next";
import type en from "./locales/en";
import type { Locale } from "./config";

export type LocaleResources = typeof en;

export const NAMESPACES = [
  "common",
  "seo",
  "home",
  "sections",
  "business",
  "company",
  "blog",
] as const satisfies readonly (keyof LocaleResources)[];

/**
 * One chunk per locale, so a visitor only downloads their own language.
 * Typing every loader as LocaleResources makes a locale that is missing a
 * key fail type-checking.
 */
const loaders: Record<Locale, () => Promise<{ default: LocaleResources }>> = {
  en: () => import("./locales/en"),
  "pt-BR": () => import("./locales/pt-BR"),
  "pt-PT": () => import("./locales/pt-PT"),
  fr: () => import("./locales/fr"),
  de: () => import("./locales/de"),
  es: () => import("./locales/es"),
  it: () => import("./locales/it"),
};

export async function loadLocaleResources(
  locale: Locale,
): Promise<LocaleResources> {
  return (await loaders[locale]()).default;
}

/**
 * A fresh instance per call (never the i18next global): the prerender step
 * renders every locale in one process and must not share language state.
 * Resources are passed in, so init is synchronous and hydration matches.
 */
export function createLandingI18n(
  locale: Locale,
  resources: LocaleResources,
): i18n {
  const instance = createInstance();
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: false,
    supportedLngs: [locale],
    resources: { [locale]: resources },
    ns: NAMESPACES,
    defaultNS: "common",
    interpolation: { escapeValue: false },
    initAsync: false,
    react: { useSuspense: false },
    showSupportNotice: false,
  });
  return instance;
}
